import React, { useState, useEffect, useRef } from 'react';
import {
  IonContent,
  IonPage,
  IonAlert,
  IonHeader,
  IonToolbar,
  IonAvatar,
} from '@ionic/react';
import { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useHistory, useParams } from 'react-router-dom';
import OpenAI from 'openai';
import { auth, db } from '../firebaseConfig';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useTranslation } from 'react-i18next';
import 'swiper/css';
import NavBar from './NavBar';

import olha from "../assets/Teacher11.png";
import anton from "../assets/Teacher1.png";

import { ChatInterface, Message } from './RussianTutor/ChatInterface';
import { AlphabetQuestion } from './RussianTutor/AlphabetQuestion';

const openai = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

export interface RussianTutorParams {
  teacherId: string;
}

const slideOpts = {
  initialSlide: 0,
  speed: 400,
  allowSlidePrev: false,
  allowSlideNext: false,
  allowTouchMove: false
};

const RussianTutor: React.FC = () => {
  const { teacherId } = useParams<RussianTutorParams>();
  const { t } = useTranslation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tokens, setTokens] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [user] = useAuthState(auth);
  const history = useHistory();
  const [knowsRussianAlphabet, setKnowsRussianAlphabet] = useState<boolean | null>(null);
  const [hasUnlimitedTokens, setHasUnlimitedTokens] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const swiperRef = useRef<SwiperType>();

  useEffect(() => {
    if (user) {
      fetchUserData();
    }
  }, [user, t, teacherId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };

  const fetchUserData = async () => {
    if (user) {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setTokens(userData.tokens || 0);
        setHasUnlimitedTokens(userData.hasUnlimitedTokens || false);
        setKnowsRussianAlphabet(userData.knowsRussianAlphabet ?? null);
        if (userData.chatHistory && userData.chatHistory[teacherId]) {
          setMessages(userData.chatHistory[teacherId] as Message[]);
          if (swiperRef.current) {
            swiperRef.current.allowSlideNext = true;
            swiperRef.current.slideTo(1, 0);
            swiperRef.current.allowSlideNext = false;
          }
        } else if (userData.knowsRussianAlphabet !== null) {
          const initialMessages: Message[] = [
            { 
              role: 'assistant',
              content: t('russianTutor.initialMessage', { teacher: teacherId === 'anton' ? 'Anton' : 'Olha' })
            }
          ];
          setMessages(initialMessages);
          if (swiperRef.current) {
            swiperRef.current.allowSlideNext = true;
            swiperRef.current.slideTo(1, 0);
            swiperRef.current.allowSlideNext = false;
          }
        }
      }
    }
  };

  const updateUserData = async (newMessages: Message[], usedTokens: number) => {
    if (user) {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data() || {};
      const updateData: any = {
        chatHistory: {
          ...userData.chatHistory,
          [teacherId]: newMessages
        }
      };

      if (!hasUnlimitedTokens) {
        updateData.tokens = tokens - usedTokens;
        setTokens(tokens - usedTokens);
      }

      if (knowsRussianAlphabet !== null) {
        updateData.knowsRussianAlphabet = knowsRussianAlphabet;
      }
      await updateDoc(userRef, updateData);
    }
  };

  const sendMessage = async (content: string) => {
    if (content.trim() === '') return;
    if (!hasUnlimitedTokens && tokens < 1) {
      setShowAlert(true);
      return;
    }

    setIsLoading(true);
    const newMessages: Message[] = [...messages, { role: 'user', content: content }];
    setMessages(newMessages);

    try {
      const response = await openai.chat.completions.create({
        model: 'gpt-3.5-turbo',
        messages: [
          { 
            role: 'system', 
            content: `You are a helpful Russian language tutor named ${teacherId === 'anton' ? 'Anton' : 'Olha'}. 
                     The student ${knowsRussianAlphabet ? 'knows' : 'does not know'} the Russian alphabet. 
                     ${knowsRussianAlphabet ? 'Use Cyrillic script freely.' : 'Use Latin transliteration when introducing new Russian words or phrases.'}
                     Respond in Russian and provide translations when necessary.
                     You must only respond to topics related to learning the Russian language.
                     If the user asks about topics unrelated to Russian language learning, politely redirect them to Russian language topics.` 
          },
          ...newMessages
        ],
      });

      const assistantReply = response.choices[0].message.content;
      if (assistantReply) {
        const updatedMessages: Message[] = [...newMessages, { role: 'assistant', content: assistantReply }];
        setMessages(updatedMessages);
        const usedTokens = response.usage?.total_tokens || 1;
        await updateUserData(updatedMessages, usedTokens);
      }
    } catch (error) {
      console.error('Error calling OpenAI API:', error);
      setMessages([...newMessages, { role: 'assistant', content: t('russianTutor.errorMessage') }]);
    }

    setIsLoading(false);
  };

  const handleAlphabetKnowledge = async (knows: boolean) => {
    setKnowsRussianAlphabet(knows);
    if (user) {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, { knowsRussianAlphabet: knows });
    }
    
    const initialMessages: Message[] = [
      { 
        role: 'assistant' as const, 
        content: knows 
          ? t('russianTutor.knowsAlphabet') 
          : t('russianTutor.doesNotKnowAlphabet')
      },
      {
        role: 'assistant' as const,
        content: t('russianTutor.initialMessage', { teacher: teacherId === 'anton' ? 'Anton' : 'Olha' })
      }
    ];
    
    setMessages(initialMessages);
    
    if (swiperRef.current) {
      swiperRef.current.allowSlideNext = true;
      swiperRef.current.slideNext();
      swiperRef.current.allowSlideNext = false;
    }
  };

  const handleSubscriptionRedirect = () => {
    history.push('/payment');
    setShowAlert(false);
  };

  return (
    <IonPage className="bg-black pb-10">
      <IonHeader>
        <IonToolbar color="dark" className="border-b border-[#31D526]/30">
          <div className="flex items-center px-4 py-2">
            <IonAvatar className="w-12 h-12 mr-3">
              <img src={teacherId === 'anton' ? anton : olha} alt="Tutor" className="w-full h-full object-cover rounded-full border-2 border-[#31D526]" />
            </IonAvatar>
            <div className="flex flex-col">
              <span className="text-[#31D526] font-medium text-xl">{ teacherId === 'anton' ? 'Anton' : 'Olha' }</span>
            </div>
          </div>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding pb-20" style={{ '--background': 'black' }}>
        <Swiper
          {...slideOpts}
          onSwiper={(swiper: SwiperType) => {
            swiperRef.current = swiper;
          }}
          className="h-full"
        >
          <SwiperSlide>
            {knowsRussianAlphabet === null && (
              <AlphabetQuestion onAnswer={handleAlphabetKnowledge} />
            )}
          </SwiperSlide>

          <SwiperSlide>
            <ChatInterface
              messages={messages}
              isLoading={isLoading}
              teacherId={teacherId}
              onSendMessage={sendMessage}
              contentRef={contentRef}
            />
          </SwiperSlide>
        </Swiper>

        <IonAlert
          isOpen={showAlert}
          onDidDismiss={() => setShowAlert(false)}
          header={t('russianTutor.outOfTokens.title')}
          message={t('russianTutor.outOfTokens.message')}
          buttons={[
            {
              text: t('russianTutor.outOfTokens.btn-cancel'),
              role: 'cancel'
            },
            {
              text: t('russianTutor.outOfTokens.btn-approve'),
              handler: handleSubscriptionRedirect
            }
          ]}
          cssClass="custom-alert"
        />
      </IonContent>
      <NavBar />
    </IonPage>
  );
};

export default RussianTutor;
